import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-xl text-card-foreground transition-all duration-300 relative overflow-hidden",
  {
    variants: {
      variant: {
        default: "border border-white/10 bg-card shadow-sm hover:shadow-md hover:border-white/20",
        glass: "bg-white/5 backdrop-blur-md border border-white/10 shadow-xl hover:shadow-2xl hover:bg-white/10",
        elevated: "bg-white/10 shadow-xl hover:shadow-2xl hover:-translate-y-1 border border-white/20",
        premium: "bg-gradient-to-br from-white/5 to-white/10 border border-eskerium-gold/20 shadow-xl hover:shadow-2xl hover:shadow-eskerium-gold/10",
        neoGlass: "bg-gradient-to-b from-white/10 to-black/30 backdrop-blur-md border border-white/10 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.7)] hover:shadow-[0_15px_40px_-15px_rgba(0,0,0,0.9)] hover:border-white/20",
      },
      accentColor: {
        none: "",
        purple: "before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-purple-500 before:shadow-[0_0_20px_5px_rgba(168,85,247,0.7)] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-purple-500/20 after:to-transparent after:pointer-events-none",
        blue: "before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-blue-500 before:shadow-[0_0_20px_5px_rgba(59,130,246,0.7)] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-blue-500/20 after:to-transparent after:pointer-events-none",
        pink: "before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-pink-500 before:shadow-[0_0_20px_5px_rgba(236,72,153,0.7)] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-pink-500/20 after:to-transparent after:pointer-events-none",
        green: "before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-emerald-500 before:shadow-[0_0_20px_5px_rgba(16,185,129,0.7)] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-emerald-500/20 after:to-transparent after:pointer-events-none",
        cyan: "before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-cyan-500 before:shadow-[0_0_20px_5px_rgba(34,211,238,0.7)] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-cyan-500/20 after:to-transparent after:pointer-events-none",
        orange: "before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[2px] before:bg-orange-500 before:shadow-[0_0_20px_5px_rgba(249,115,22,0.7)] after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-orange-500/20 after:to-transparent after:pointer-events-none",
      }
    },
    defaultVariants: {
      variant: "default",
      accentColor: "none",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, accentColor, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, accentColor }), className)}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0 relative z-10", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }